import type { ChangeEvent } from 'react';
import React from 'react';

import { Divider, FormControlLabel, Typography } from '@mui/material';
import type { ShareDocumentDialogAction, ShareDocumentDialogState } from 'src/components';
import {
  AlineCheckbox,
  AlineTextarea,
  DocumentForwardingToggle,
  PermissionList,
  ShareDocumentDialogActionType,
} from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  accessText: {
    margin: theme.spacing(2, 0),
  },
  checkBox: {
    margin: theme.spacing(1, 0),
  },
  textArea: {
    flexGrow: 1,
  },
}));

export interface ShareDocumentDialogDocument extends Pick<
  Apollo.Document,
  'id' | 'forwardingPermissionRole' | 'requesterRole' | '__typename'
> {
  owner: Pick<Apollo.User, '__typename' | 'id'>;
}

interface ShareDialogContentProps {
  autocompleteEmpty: boolean;
  dialogDispatch: React.Dispatch<ShareDocumentDialogAction>;
  dialogState: ShareDocumentDialogState;
  document: ShareDocumentDialogDocument;
  disabled?: boolean;
}

export function ShareDocumentDialogDetails({
  autocompleteEmpty,
  dialogDispatch,
  dialogState,
  disabled,
  document,
}: ShareDialogContentProps): JSX.Element {
  const { classes } = useStyles();
  const { message, notifyRecipients } = dialogState;

  function handleChangeTextArea(event: ChangeEvent<HTMLTextAreaElement>): void {
    dialogDispatch({
      payload: { message: event.target.value },
      type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
    });
  }

  if (autocompleteEmpty)
    return (
      <>
        <Typography className={classes.accessText} variant="subtitle1">
          General access
        </Typography>
        <Divider variant="fullWidth" />
        <PermissionList disabled={disabled} documentId={document.id} />
        <Divider variant="fullWidth" />
        <Typography className={classes.accessText} variant="subtitle1">
          Document forwarding
        </Typography>
        <DocumentForwardingToggle document={document} />
      </>
    );

  return (
    <>
      <FormControlLabel
        className={classes.checkBox}
        control={
          <AlineCheckbox
            checked={notifyRecipients}
            color="primary"
            onChange={(): void => {
              dialogDispatch({
                payload: { notifyRecipients: !notifyRecipients },
                type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
              });
            }}
          />
        }
        label="Notify recipients"
      />
      {notifyRecipients && (
        <AlineTextarea
          className={classes.textArea}
          maxRows={10}
          minRows={10}
          value={message}
          onChange={handleChangeTextArea}
        />
      )}
    </>
  );
}
